import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Select } from '../components/Select.js';
import { adminApi, type ApiKey, type CredentialGrant } from '../lib/api.js';
import { useAction } from '../lib/useAsync.js';
import { useSession } from '../lib/auth-client.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { Button, Field, Note, OutcomeNote, inputStyle } from '../components/ui.js';
import {
  GRANTS,
  MAX_EXPIRY_DAYS,
  MAX_NAME_LENGTH,
  parseExpiry,
  useIssuableRoles,
} from './IssueCredential.js';

/**
 * Change what an existing service credential may do.
 *
 * ── Why this exists ──────────────────────────────────────────────────
 *
 * A grant used to mean a new key: permissions were only written at issue, so
 * giving keyodo `subscriptions:read` meant minting a second credential and
 * swapping a secret on every service holding the first. The auth service reads
 * a key's permissions on every request with nothing cached in front of them, so
 * a saved change is in force on the key's next call and the secret never moves.
 *
 * ── The whole set, not a delta ───────────────────────────────────────
 *
 * The service replaces the list. That is why `CredentialGrant` has to name every
 * grant the service offers, and why the button is withheld for a key holding
 * permissions no grant describes: saving from this list would drop them.
 */

/**
 * Whether the edit button belongs on a row.
 *
 * Every condition here is one the server refuses, checked first so the refusal
 * is a missing button rather than an error after the click. `grants` absent
 * means an auth service older than this editor, which would 404 the save.
 */
export function canEditGrants(k: ApiKey): boolean {
  const appSlug = k.metadata?.appSlug;
  return (
    k.enabled !== false &&
    Array.isArray(k.grants) &&
    k.unmanagedPermissions !== true &&
    typeof appSlug === 'string' &&
    appSlug !== ''
  );
}

/** What a save would add and remove. Order-insensitive, so ticking and unticking
 *  the same box reads as no change. */
export function grantChange(
  current: readonly CredentialGrant[],
  next: readonly CredentialGrant[],
): { added: CredentialGrant[]; removed: CredentialGrant[] } {
  return {
    added: next.filter((g) => !current.includes(g)),
    removed: current.filter((g) => !next.includes(g)),
  };
}

/**
 * Whether the Edit button belongs on a row at all: a live service credential.
 * Name, role and expiry are editable on any such key; the permissions section
 * inside is governed separately by `canEditGrants`.
 */
export function canEditCredential(k: ApiKey): boolean {
  const appSlug = k.metadata?.appSlug;
  return k.enabled !== false && typeof appSlug === 'string' && appSlug !== '';
}

export type ExpiryChoice = 'keep' | 'days' | 'never';

/**
 * The PATCH body a save should send, or `null` when nothing but the grants (or
 * nothing at all) changed. Only fields that differ are sent, so a save that
 * renames a key does not also re-date it or re-check its role.
 */
export function detailChanges(
  current: { name: string | null; role: string | null },
  form: { name: string; role: string; expiry: ExpiryChoice; days?: number },
): { name?: string; role?: string | null; expiresInDays?: number | null } | null {
  const out: { name?: string; role?: string | null; expiresInDays?: number | null } = {};
  const name = form.name.trim();
  if (name !== '' && name !== (current.name ?? '')) out.name = name;
  const role = form.role === '' ? null : form.role;
  if (role !== current.role) out.role = role;
  if (form.expiry === 'never') out.expiresInDays = null;
  if (form.expiry === 'days' && form.days !== undefined) out.expiresInDays = form.days;
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Edit a service credential: its name, what it may do, the role it acts with,
 * and when it expires (Jon, 2026-09-23).
 *
 * Replaced the permissions-only dialog. Two requests behind one Save, because
 * the service keeps them as two endpoints: `PUT …/grants` for the whole grant
 * set and `PATCH …/:id` for the rest. Details go first, since a refused role is
 * the likelier failure and should stop the save before the grants move.
 *
 * `agentRoles` is set when the key belongs to an agent: the service refuses a
 * role the agent does not hold, so the picker only offers those.
 */
export function EditCredential({
  apiKey,
  onClose,
  onSaved,
  agentRoles,
}: {
  apiKey: ApiKey;
  onClose: () => void;
  onSaved: () => void;
  agentRoles?: string[];
}) {
  const keyRole = typeof apiKey.metadata?.role === 'string' && apiKey.metadata.role ? apiKey.metadata.role : null;
  const grantsEditable = canEditGrants(apiKey);

  const [baseline, setBaseline] = useState({
    name: apiKey.name,
    role: keyRole,
    grants: apiKey.grants ?? [],
    expiresAt: apiKey.expiresAt,
  });
  const [name, setName] = useState(apiKey.name ?? '');
  const [role, setRole] = useState(keyRole ?? '');
  const [grants, setGrants] = useState<CredentialGrant[]>(apiKey.grants ?? []);
  const [expiry, setExpiry] = useState<ExpiryChoice>('keep');
  const [daysText, setDaysText] = useState('90');
  const action = useAction();
  const { data: session } = useSession();

  const issuable = useIssuableRoles(session?.user.role);
  const offered = agentRoles ? issuable.filter((r) => agentRoles.includes(r)) : issuable;
  /* The key's current role stays in the list even when it could not be given
     today (a key issued before its agent was moved down), so the picker shows
     what is true rather than silently reading "No role". */
  const roleOptions = keyRole && !offered.includes(keyRole) ? [keyRole, ...offered] : offered;

  const { days, valid: daysValid } = parseExpiry(daysText);
  const expiryValid = expiry !== 'days' || (daysValid && days !== undefined);
  const details = detailChanges(
    { name: baseline.name, role: baseline.role },
    { name, role, expiry, ...(days !== undefined ? { days } : {}) },
  );
  const { added, removed } = grantChange(baseline.grants, grants);
  const grantsChanged = grantsEditable && (added.length > 0 || removed.length > 0);
  const changed = details !== null || grantsChanged;
  const nameValid = name.trim() !== '' && name.trim().length <= MAX_NAME_LENGTH;
  const appSlug = typeof apiKey.metadata?.appSlug === 'string' ? apiKey.metadata.appSlug : '';
  const label = baseline.name ?? 'this key';

  return (
    <Modal
      title={`Edit ${label}`}
      subtitle={`Scoped to ${appSlug}. Changes apply from the key's next request; the secret stays the same.`}
      onClose={onClose}
      footer={
        <ModalActions>
          <Button onClick={onClose}>{changed ? 'Cancel' : 'Close'}</Button>
          <Button
            disabled={
              !changed ||
              !nameValid ||
              !expiryValid ||
              (grantsEditable && grants.length === 0) ||
              action.running
            }
            onClick={() =>
              void action.run(async () => {
                let next = baseline;
                if (details) {
                  const r = await adminApi.updateApiKey(apiKey.id, details);
                  next = { ...next, name: r.name, role: r.role, expiresAt: r.expiresAt };
                }
                if (grantsChanged) {
                  const r = await adminApi.setApiKeyGrants(apiKey.id, grants);
                  next = { ...next, grants: r.grants };
                }
                setBaseline(next);
                setName(next.name ?? '');
                setRole(next.role ?? '');
                setGrants(next.grants);
                setExpiry('keep');
                onSaved();
                return `Saved. ${next.name ?? 'The key'} has the new settings from its next request.`;
              })
            }
          >
            {action.running ? 'Saving…' : 'Save'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.9rem', marginTop: '.4rem' }}>
        <Field label="Name">
          <input
            style={inputStyle}
            value={name}
            maxLength={MAX_NAME_LENGTH}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '.45rem' }}>
          <span style={{ fontWeight: 600, color: C.textMid, fontSize: '.85rem' }}>Capabilities</span>
          {grantsEditable ? (
            GRANTS.map((g) => (
              <label
                key={g.value}
                style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', fontSize: '.85rem' }}
              >
                <input
                  type="checkbox"
                  checked={grants.includes(g.value)}
                  style={{ marginTop: '.2rem' }}
                  onChange={(e) =>
                    setGrants((cur) =>
                      e.target.checked ? [...cur, g.value] : cur.filter((v) => v !== g.value),
                    )
                  }
                />
                <span>
                  <strong>{g.label}</strong>
                  <code style={{ color: C.muted, marginLeft: '.4rem', fontSize: '.78rem' }}>{g.value}</code>
                  <div style={{ color: C.muted, fontSize: '.78rem', lineHeight: 1.4 }}>{g.hint}</div>
                </span>
              </label>
            ))
          ) : (
            <Note>
              This key holds permissions outside the list, so they cannot be edited here without
              dropping them. Its name, role and expiry can still be changed.
            </Note>
          )}
        </div>

        {grantsEditable && grants.length === 0 && (
          <Note tone="danger">A key needs at least one capability. To stop it working, revoke it instead.</Note>
        )}
        {grantsChanged && added.length > 0 && (
          <Note tone="warn">
            Adding {added.join(', ')} gives it to every copy of this key, including any that has
            leaked. It is the same decision as issuing a key with it.
          </Note>
        )}
        {grantsChanged && removed.length > 0 && (
          <Note tone="warn">
            Removing {removed.join(', ')} breaks any call {appSlug} makes that relies on it, from its
            next request.
          </Note>
        )}

        <Field
          label="Acts with the role"
          hint={
            agentRoles
              ? 'Only roles this agent holds and you may grant are offered.'
              : 'Only roles you may grant are offered.'
          }
        >
          <Select
            block
            label="Role"
            value={role}
            onChange={setRole}
            options={[
              { value: '', label: 'No role' },
              ...roleOptions.map((r) => ({
                value: r,
                label: r === keyRole && !offered.includes(r) ? `${r} (current, cannot be given again)` : r,
              })),
            ]}
          />
        </Field>

        <Field
          label="Expiry"
          hint={
            baseline.expiresAt
              ? `Currently expires ${new Date(baseline.expiresAt).toLocaleDateString()}.`
              : 'Currently never expires.'
          }
        >
          <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <Select<ExpiryChoice>
                block
                label="Expiry"
                value={expiry}
                onChange={setExpiry}
                options={[
                  { value: 'keep', label: 'Keep as it is' },
                  { value: 'days', label: 'Expire a number of days from now' },
                  { value: 'never', label: 'Never expire' },
                ]}
              />
            </div>
            {expiry === 'days' ? (
              <input
                style={{ ...inputStyle, width: '6rem' }}
                value={daysText}
                inputMode="numeric"
                aria-label="Days from now"
                onChange={(e) => setDaysText(e.target.value)}
              />
            ) : null}
          </div>
        </Field>
        {expiry === 'days' && !expiryValid && (
          <Note tone="danger">Days must be a whole number, 1 to {MAX_EXPIRY_DAYS}.</Note>
        )}
      </div>
    </Modal>
  );
}
